import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  Paper,
  Stack,
  Typography
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useVisitorConsentByToken } from "../../hooks/Remote/Schedule/useVisitorConsentByToken";
import { useRemoteConfirmConsent } from "../../hooks/Remote/Schedule/useRemoteConfirmConsent";
import { buildVisitConsentConfirmPayload } from "./visit-consent-confirm-payload.build";
import { applyVisitorConsentDocumentCheckboxChange } from "./visitor-consent-document-checkbox.handlers";
import {
  visitorConsentHandleConfirm,
  visitorConsentHandleNext
} from "./visitor-consent-page-actions";
import { mapVisitConsentPublicErrorKey } from "./visit-consent-public-error.map";
import {
  mapVisitConsentContentFromSession,
  mapVisitConsentDocumentsFromSession
} from "./visit-consent-session.mapper";
import { VisitorConsentPageStatus } from "./visitor-consent-page-status.partial";
import { CanvasSignaturePad } from "../../components/SignaturePad/canvas-signature-pad.component";
import { VisitorConsentDocumentAttachmentPreview } from "./visitor-consent-document-attachment-preview.component";

type VisitorConsentRouteParams = {
  token: string;
};

const companyLogoSx = (compact: boolean) =>
  compact
    ? {
        maxWidth: { xs: 200, md: 240 },
        maxHeight: { xs: 64, md: 80 },
        width: "auto",
        objectFit: "contain" as const
      }
    : {
        maxWidth: { xs: 260, md: 320 },
        maxHeight: { xs: 88, md: 112 },
        width: "auto",
        objectFit: "contain" as const
      };

const VisitorConsentPage: React.FC = () => {
  const { t, i18n } = useTranslation();

  const { token } = useParams<VisitorConsentRouteParams>();

  /** static color; we cannot use the theme color here because it's public route */
  const buttonColor = "#1976d2";

  const [isIntroPage, setIsIntroPage] = useState(true);
  const [currentDocumentIndex, setCurrentDocumentIndex] = useState(0);
  const [documentConsents, setDocumentConsents] = useState<Record<string, boolean>>({});
  const [signaturePngBase64, setSignaturePngBase64] = useState<string | null>(null);
  const [signingMoment, setSigningMoment] = useState<Date | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [confirmSuccess, setConfirmSuccess] = useState(false);

  const sessionQuery = useVisitorConsentByToken(token);
  const published = useMemo(
    () =>
      sessionQuery.data
        ? mapVisitConsentContentFromSession(sessionQuery.data.content)
        : { title: "", subtitle: "", description: "" },
    [sessionQuery.data]
  );
  const documentList = useMemo(
    () =>
      sessionQuery.data ? mapVisitConsentDocumentsFromSession(sessionQuery.data.documents) : [],
    [sessionQuery.data]
  );

  useEffect(() => {
    const n = documentList.length;
    if (n === 0) {
      setCurrentDocumentIndex(0);
      setIsIntroPage(true);
      return;
    }

    setCurrentDocumentIndex(prev => (prev >= n ? n - 1 : prev));
  }, [documentList]);

  const isLastDocument =
    documentList.length > 0 && currentDocumentIndex === documentList.length - 1;

  useEffect(() => {
    if (!isIntroPage && isLastDocument) {
      setSigningMoment(prev => prev ?? new Date());
      return;
    }
    setSigningMoment(null);
    setSignaturePngBase64(null);
  }, [isIntroPage, isLastDocument]);

  const currentDocument =
    documentList.length === 0 ? null : documentList[currentDocumentIndex] ?? documentList[0];
  const confirmConsentMutation = useRemoteConfirmConsent();

  const isPreviousDisabled = isIntroPage;
  const isConfirmStep = !isIntroPage && isLastDocument;
  const pageIndicator = isIntroPage
    ? t("_visitorConsent_introPageLabel")
    : t("_visitorConsent_documentCounter", {
        current: currentDocumentIndex + 1,
        total: documentList.length
      });

  const handlePrevious = () => {
    setSubmitError(null);

    if (currentDocumentIndex === 0) {
      setIsIntroPage(true);
      return;
    }

    setCurrentDocumentIndex(prev => prev - 1);
  };

  const handleNext = () => {
    visitorConsentHandleNext({
      setSubmitError,
      isIntroPage,
      documentListLength: documentList.length,
      setIsIntroPage,
      currentDocumentIndex,
      setCurrentDocumentIndex
    });
  };

  /** Merged with `documentConsents` and `signingMoment` in `buildVisitConsentConfirmPayload` for `POST /visit/consent/confirm`. */
  const handleSignatureChange = useCallback((value: string | null) => {
    setSignaturePngBase64(value);
  }, []);

  const pendingConfirmPayload = useMemo(
    () =>
      buildVisitConsentConfirmPayload({
        token,
        documentList,
        documentConsents,
        signaturePngBase64,
        signingMoment
      }),
    [token, documentList, documentConsents, signaturePngBase64, signingMoment]
  );

  const canSubmitConfirm = pendingConfirmPayload !== null;

  const formattedSigningDate = useMemo(() => {
    if (!signingMoment) {
      return "";
    }
    const localeTag = i18n.language?.toLowerCase().startsWith("de") ? "de-DE" : "en-GB";
    return new Intl.DateTimeFormat(localeTag, {
      dateStyle: "full",
      timeStyle: "short"
    }).format(signingMoment);
  }, [i18n.language, signingMoment]);

  const handleConfirm = async () => {
    await visitorConsentHandleConfirm({
      setSubmitError,
      pendingConfirmPayload,
      mutateAsync: confirmConsentMutation.mutateAsync,
      t,
      onSuccess: () => setConfirmSuccess(true)
    });
  };

  const handlePrimaryAction = async () => {
    if (isConfirmStep) {
      await handleConfirm();
      return;
    }

    handleNext();
  };

  const currentDocumentConsent = currentDocument
    ? documentConsents[currentDocument.id] ?? false
    : false;

  const handleDocumentConsentChange = (checked: boolean) => {
    applyVisitorConsentDocumentCheckboxChange({
      currentDocument,
      checked,
      setDocumentConsents
    });
  };

  if (!token?.trim()) {
    return <VisitorConsentPageStatus variant="error" messageKey="_visitorConsent_invalidLink" />;
  }

  if (sessionQuery.isLoading || sessionQuery.isIdle) {
    return (
      <VisitorConsentPageStatus variant="loading" messageKey="_visitorConsent_sessionLoading" />
    );
  }

  if (sessionQuery.isError) {
    return (
      <VisitorConsentPageStatus
        variant="error"
        messageKey={mapVisitConsentPublicErrorKey(sessionQuery.error)}
      />
    );
  }

  if (confirmSuccess || sessionQuery.data.consentStatus === "complete") {
    return (
      <VisitorConsentPageStatus
        variant="success"
        messageKey="_visitorConsent_confirmSuccess"
        logoUrl={sessionQuery.data.companyLogoUrl}
      />
    );
  }

  if (documentList.length === 0) {
    return (
      <VisitorConsentPageStatus
        variant="error"
        messageKey="_visitorConsent_noDocumentsConfigured"
        logoUrl={sessionQuery.data.companyLogoUrl}
      />
    );
  }

  return (
    <Box
      data-testid="visitor-consent-page"
      sx={{
        minHeight: "100dvh",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        justifyContent: "stretch",
        p: 0,
        bgcolor: "background.default"
      }}
    >
      <Paper
        elevation={2}
        sx={{
          width: "100%",
          maxWidth: { xs: "100%", md: 960 },
          minHeight: "100dvh",
          height: "100dvh",
          mx: "auto",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          borderRadius: { xs: 0, md: 2 }
        }}
      >
        <Box
          data-testid="visitor-consent-page-content"
          sx={{
            m: { xs: 1, md: 1.5 },
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            display: "flex",
            flexDirection: "column",
            flex: 1,
            overflow: "hidden",
            bgcolor: "#ffffff"
          }}
        >
          {/* Title and description */}
          <Box
            sx={{ px: { xs: 2, md: 3 }, py: isIntroPage ? 2.5 : 1 }}
            data-testid="visitor-consent-header"
          >
            <Box
              sx={{
                display: "grid",
                columnGap: 1,
                rowGap: isIntroPage ? 1.5 : 0,
                gridTemplateColumns: sessionQuery.data?.companyLogoUrl
                  ? { xs: "1fr", md: "minmax(0, 10fr) minmax(0, 2fr)" }
                  : "1fr"
              }}
            >
              <Stack spacing={isIntroPage ? 1.5 : 0} sx={{ minWidth: 0 }}>
                <Typography
                  component="h1"
                  variant="h5"
                  sx={{
                    width: "100%",
                    minWidth: 0
                  }}
                >
                  {published.title}
                </Typography>
                {isIntroPage ? (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ width: "100%", maxWidth: "100%" }}
                  >
                    {published.subtitle}
                  </Typography>
                ) : null}
              </Stack>
              {sessionQuery.data?.companyLogoUrl ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: { xs: "center", md: isIntroPage ? "flex-end" : "center" },
                    justifyContent: { xs: "flex-start", md: "flex-end" }
                  }}
                >
                  <Box
                    component="img"
                    src={sessionQuery.data.companyLogoUrl}
                    alt="Company logo"
                    data-testid="visitor-consent-company-logo"
                    sx={companyLogoSx(!isIntroPage)}
                  />
                </Box>
              ) : null}
            </Box>
          </Box>

          <Divider />

          <Box
            data-testid="visitor-consent-reader"
            sx={{
              flex: 1,
              overflowY: "auto",
              px: { xs: 2, md: 4 },
              py: 3
            }}
          >
            <Stack spacing={2}>
              <Typography variant="h6" data-testid="visitor-consent-document-title">
                {isIntroPage ? t("_visitorConsent_introTitle") : currentDocument?.title ?? ""}
              </Typography>
              {isIntroPage ? (
                <Typography
                  variant="body1"
                  sx={{ whiteSpace: "pre-line", lineHeight: 1.75 }}
                  data-testid="visitor-consent-document-body"
                >
                  {published.description}
                </Typography>
              ) : (
                currentDocument && (
                  <Stack spacing={2} data-testid="visitor-consent-document-body">
                    {currentDocument.description.trim().length > 0 && (
                      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.75 }}>
                        {currentDocument.description}
                      </Typography>
                    )}
                    {currentDocument.fileViewerUrl && (
                      <VisitorConsentDocumentAttachmentPreview
                        fileViewerUrl={currentDocument.fileViewerUrl}
                        fileName={currentDocument.fileName}
                        openLinkLabel={t("_visitorConsent_viewAttachedDocument")}
                        pdfPreviewUnavailableMessage={t(
                          "_visitorConsent_attachmentPreviewUnavailable"
                        )}
                        pdfPreviewLoadingMessage={t("_visitorConsent_attachmentPreviewLoading")}
                        pdfPreviewHeight="min(55vh, 520px)"
                        linkButtonSx={{ color: buttonColor, borderColor: buttonColor }}
                      />
                    )}
                    {currentDocument.body.trim().length > 0 && (
                      <Typography variant="body1" sx={{ whiteSpace: "pre-line", lineHeight: 1.75 }}>
                        {currentDocument.body}
                      </Typography>
                    )}
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={currentDocumentConsent}
                          onChange={event => handleDocumentConsentChange(event.target.checked)}
                          sx={{
                            color: "#000000",
                            "&.Mui-checked": {
                              color: "#000000"
                            }
                          }}
                        />
                      }
                      label={t("_visitorConsent_documentCheckboxLabel", {
                        documentTitle: currentDocument.title
                      })}
                    />
                    {isLastDocument && (
                      <Stack
                        spacing={2}
                        sx={{ mt: 2, pt: 2, borderTop: "1px solid", borderColor: "divider" }}
                        data-testid="visitor-consent-signature-block"
                      >
                        <Typography variant="h6">
                          {t("_visitorConsent_signatureSectionTitle")}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {t("_visitorConsent_signaturePrompt")}
                        </Typography>

                        {/* Signature pad */}
                        <CanvasSignaturePad
                          clearLabel={t("_visitorConsent_signatureClear")}
                          formattedSigningDate={formattedSigningDate}
                          onSignatureChange={handleSignatureChange}
                          signingDateLabel={t("_visitorConsent_signatureDateLabel")}
                          testIds={{
                            root: "visitor-consent-signature-pad",
                            canvas: "visitor-consent-signature-canvas",
                            clear: "visitor-consent-signature-clear",
                            signingDate: "visitor-consent-signing-date"
                          }}
                        />
                      </Stack>
                    )}
                  </Stack>
                )
              )}
            </Stack>
          </Box>

          <Divider />

          {/* Previous and next buttons */}
          <Box
            data-testid="visitor-consent-buttons"
            sx={{
              position: "sticky",
              bottom: 0,
              backgroundColor: "background.paper",
              px: { xs: 2, md: 3 },
              py: 2
            }}
          >
            <Box
              sx={{
                display: "grid",
                width: "100%",
                alignItems: "center",
                columnGap: 2,
                rowGap: { xs: 1.25, md: 0 },
                gridTemplateColumns: { xs: "1fr 1fr", md: "auto minmax(0, 1fr) auto" },
                gridTemplateRows: { xs: "auto auto", md: "auto" }
              }}
            >
              <Typography
                variant="body2"
                color="text.secondary"
                data-testid="visitor-consent-index"
                sx={{
                  gridColumn: { xs: "1 / -1", md: 2 },
                  gridRow: { xs: 1, md: 1 },
                  textAlign: "center",
                  minWidth: 0,
                  px: { xs: 0, md: 1 },
                  alignSelf: "center",
                  justifySelf: "stretch"
                }}
              >
                {pageIndicator}
              </Typography>
              <Button
                disabled={isPreviousDisabled}
                onClick={handlePrevious}
                data-testid="visitor-consent-prev"
                sx={{
                  backgroundColor: buttonColor,
                  gridColumn: { xs: 1, md: 1 },
                  gridRow: { xs: 2, md: 1 },
                  justifySelf: "stretch"
                }}
              >
                {t("_visitorConsent_previous")}
              </Button>
              <Button
                disabled={
                  confirmConsentMutation.isLoading ||
                  (isIntroPage && documentList.length === 0) ||
                  (isConfirmStep && !canSubmitConfirm)
                }
                onClick={handlePrimaryAction}
                data-testid="visitor-consent-next"
                sx={{
                  backgroundColor: buttonColor,
                  gridColumn: { xs: 2, md: 3 },
                  gridRow: { xs: 2, md: 1 },
                  justifySelf: "stretch"
                }}
              >
                {confirmConsentMutation.isLoading
                  ? t("_visitorConsent_confirmLoading")
                  : isConfirmStep
                    ? t("_visitorConsent_acceptAndSign")
                    : t("_visitorConsent_next")}
              </Button>
            </Box>
            {isConfirmStep && !canSubmitConfirm && !confirmConsentMutation.isLoading && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 1, display: "block" }}
                data-testid="visitor-consent-confirm-hint"
              >
                {t("_visitorConsent_confirmDisabledHint")}
              </Typography>
            )}
            {submitError && (
              <Typography
                variant="caption"
                color="error"
                sx={{ mt: 1, display: "block" }}
                data-testid="visitor-consent-submit-error"
              >
                {submitError}
              </Typography>
            )}
          </Box>
        </Box>
      </Paper>

      <Box component="span" data-testid="visitor-consent-token" sx={{ display: "none" }}>
        {token}
      </Box>
    </Box>
  );
};

export default VisitorConsentPage;
